import { useState } from 'react';
import { Check, Copy, Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import { ActivationFunnelService } from '@/services/analytics/ActivationFunnelService';
import { ActivationPackService } from '@/services/database/ActivationPackService';
import { useAssetGenerator } from '@/hooks/activation';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  APP_STORE_BADGE_SRC,
  GOOGLE_PLAY_BADGE_SRC,
} from '@/constants/appStore';
import { SendLinkToTeam } from '../shared/SendLinkToTeam';

interface PackAppOnboardingSectionProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

export const PackAppOnboardingSection = ({
  pack,
  brand = '#4E9CFF',
}: PackAppOnboardingSectionProps) => {
  const { downloadByType } = useAssetGenerator(pack);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState<'png' | 'pdf' | null>(null);

  const name = ActivationPackService.getTargetName(pack);
  const smartLink = ActivationPackService.getAppSmartLinkUrl(pack);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(smartLink);
      ActivationFunnelService.packShared(
        ActivationFunnelService.packGenieId(pack),
        'link',
        { link: 'app_smart_link' },
      );
      setCopied(true);
      toast.success('Smart link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleDownload = async (kind: 'png' | 'pdf') => {
    if (downloading) return;
    setDownloading(kind);
    try {
      await downloadByType(kind === 'png' ? 'app_poster_png' : 'app_poster_pdf');
      toast.success('Poster downloaded');
    } catch {
      toast.error("Couldn't build the poster");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <OverviewSectionCard
      title="Get your Genie on phones"
      subtitle="One smart link that sends people to the app on their phone, or straight into your Genie on the web."
      genieIcon="phone"
      brand={brand}
    >
      <div className="space-y-5">
        {/* Smart link copy row */}
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
            Your share link
          </label>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-gray-100 px-3 py-2 rounded-lg border flex-1 truncate text-gray-700">
              {smartLink}
            </code>
            <Button variant="outline" size="sm" onClick={handleCopy} className="shrink-0">
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-1.5 text-green-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-1.5" />
                  Copy
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            Send it in a text, email, or bio. We send each person to the right place for their device.
          </p>
        </div>

        <SendLinkToTeam
          packId={pack.id}
          kind="app"
          genieId={ActivationFunnelService.packGenieId(pack)}
          brand={brand}
        />

        {/* Store badges */}
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
            Download the app
          </label>
          <div className="flex items-center gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex transition-transform hover:scale-105"
              aria-label="Download on the App Store"
            >
              <img src={APP_STORE_BADGE_SRC} alt="Download on the App Store" className="h-10 w-auto" />
            </a>
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex transition-transform hover:scale-105"
              aria-label="Get it on Google Play"
            >
              <img src={GOOGLE_PLAY_BADGE_SRC} alt="Get it on Google Play" className="h-10 w-auto" />
            </a>
          </div>
        </div>

        {/* Install poster downloads */}
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">Install poster</div>
            <div className="text-xs text-gray-600">
              A branded "scan to get started" poster for {name}.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              onClick={() => handleDownload('png')}
              disabled={downloading !== null}
              className="text-white border-0"
              style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
            >
              {downloading === 'png' ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1.5" />
              )}
              Image
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleDownload('pdf')}
              disabled={downloading !== null}
            >
              {downloading === 'pdf' ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-1.5" />
              )}
              Print PDF
            </Button>
          </div>
        </div>
      </div>
    </OverviewSectionCard>
  );
};
